import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from 'input-otp';
import { type ComponentProps, useContext } from 'react';
import { cn } from '@avanta/shared/utils/cn';

const InputOTPContainer = ({
  className,
  containerClassName,
  ...props
}: ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}): React.ReactElement => (
  <OTPInput
    data-slot="input-otp"
    containerClassName={cn('flex items-center gap-2 has-disabled:opacity-50', containerClassName)}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
);

const InputOTPGroup = ({ className, ...props }: ComponentProps<'div'>): React.ReactElement => (
  <div data-slot="input-otp-group" className={cn('flex items-center', className)} {...props} />
);

const InputOTPSlot = ({
  index,
  className,
  ...props
}: ComponentProps<'div'> & {
  index: number;
}): React.ReactElement => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        'size-10 am-sm:size-12 data-[active=true]:border-teal-600 data-[active=true]:aria-invalid:ring-red-500/20 dark:data-[active=true]:aria-invalid:ring-red-500/40 aria-invalid:border-red-500 data-[active=true]:aria-invalid:border-red-500 dark:bg-input/30 relative flex items-center justify-center shadow-xs transition-all outline-none data-[active=true]:z-10 text-center text-2xl border-2 rounded-lg focus:border-teal-600 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400',
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
};

export const InputOTP = ({
  onVerifyCode,
  steps,
  disabled,
}: {
  onVerifyCode: (code: string) => void;
  steps: number;
  disabled?: boolean;
}): React.ReactElement => (
  <InputOTPContainer
    maxLength={steps}
    pattern={REGEXP_ONLY_DIGITS}
    onChange={onVerifyCode}
    containerClassName="flex justify-center"
    disabled={disabled}
  >
    <InputOTPGroup className="gap-1 am-md:gap-2">
      {Array.from({ length: steps }).map((_, index) => (
        <InputOTPSlot key={index} index={index} />
      ))}
    </InputOTPGroup>
  </InputOTPContainer>
);
